import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  Box,
  Grid,
  Card,
  CardHeader,
  IconButton,
  Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import PropTypes from 'prop-types';
import { getWebTotalSubscriber } from '../../services/reports.service';
import Datepicker from '../form-elements/datepicker.component';
import { formatDate } from '../../utils/datetime.util';
import Iconify from '../common/iconify.component';

const getSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toTimestamp = (value) => {
  if (!value) return null;
  const parsed = dayjs(value).startOf('day');
  return parsed.isValid() ? parsed.valueOf() : null;
};

const formatTooltipDate = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MMM DD, YYYY') : '';
  }
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MMM DD, YYYY') : '';
  }
  return '';
};

const getEntryCount = (entry) =>
  getSafeNumber(
    entry?.count ??
      entry?.total ??
      entry?.value ??
      entry?.subscribersCount ??
      entry?.subscriberCount
  );

const getEntryTillDateCount = (entry) =>
  getSafeNumber(
    entry?.tillDateCount ??
      entry?.till_date_count ??
      entry?.totalTillDate ??
      entry?.total_till_date ??
      entry?.cumulativeCount ??
      entry?.cumulative_count
  );

const getSummaryTotals = (data) => {
  const source =
    data?.summaryTillDate ||
    data?.summary ||
    data?.totalSummary ||
    data?.totals ||
    {};

  const monthly = getSafeNumber(
    source.monthly ?? source.monthlyCount ?? source.monthlyTotal
  );
  const yearly = getSafeNumber(
    source.yearly ?? source.yearlyCount ?? source.yearlyTotal
  );
  const total = getSafeNumber(source.total ?? monthly + yearly);

  return { monthly, yearly, total };
};

const buildAlignedSeries = (monthly, yearly) => {
  const map = new Map();

  const addToMap = (data, key) => {
    data.forEach((entry) => {
      const x = toTimestamp(entry?.date);
      if (!x) return;

      if (!map.has(x)) {
        map.set(x, {
          x,
          monthly: 0,
          yearly: 0,
          monthlyTillDate: 0,
          yearlyTillDate: 0,
        });
      }

      const point = map.get(x);
      point[key] = getEntryCount(entry);
      point[`${key}TillDate`] = getEntryTillDateCount(entry);
    });
  };

  addToMap(monthly, 'monthly');
  addToMap(yearly, 'yearly');

  const sorted = Array.from(map.values()).sort((a, b) => a.x - b.x);

  return {
    monthly: sorted.map((p) => ({
      x: p.x,
      y: p.monthlyTillDate,
      count: p.monthly,
      tillDate: p.monthlyTillDate,
    })),
    yearly: sorted.map((p) => ({
      x: p.x,
      y: p.yearlyTillDate,
      count: p.yearly,
      tillDate: p.yearlyTillDate,
    })),
  };
};

const TotalSubscriber = ({
  selectedChannel,
  startDate,
  endDate,
  updateFilters,
}) => {
  const [summary, setSummary] = useState({
    monthly: [],
    yearly: [],
    lastScheduledSyncAt: null,
    tillDateSummary: {
      monthly: 0,
      yearly: 0,
      total: 0,
    },
  });
  const [filters, setFilters] = useState({
    channel: null,
    platform: null,
    startDate,
    endDate,
    reloadCounter: 0,
    loading: true,
  });

  const fetchSummary = async () => {
    setFilters({
      ...filters,
      loading: true,
    });
    const params = {
      fromDate: formatDate(filters.startDate, 'YYYY-MM-DD'),
      toDate: formatDate(filters.endDate, 'YYYY-MM-DD'),
      channelId: selectedChannel || 0,
    };
    getWebTotalSubscriber(params)
      .then((res) => {
        const data = res.data || {};
        setSummary({
          monthly: data.monthly || [],
          yearly: data.yearly || [],
          lastScheduledSyncAt: data.lastScheduledSyncAt || null,
          tillDateSummary: getSummaryTotals(data),
        });

        setFilters({
          ...filters,
          loading: false,
        });
      })
      .catch(() => {
        setFilters({
          ...filters,
          loading: false,
        });
      });
  };

  useEffect(() => {
    fetchSummary();
  }, [
    selectedChannel,
    filters.startDate,
    filters.endDate,
    filters.reloadCounter,
  ]);

  const handleStartDateChange = (v) => {
    setFilters({
      ...filters,
      startDate: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
    updateFilters({
      startDate: v,
      endDate: filters.endDate,
    });
  };

  const handleEndDateChange = (v) => {
    setFilters({
      ...filters,
      endDate: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
    updateFilters({
      startDate: filters.startDate,
      endDate: v,
    });
  };

  const handleRefreshData = () => {
    setFilters({
      ...filters,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };

  const lineSeries = useMemo(() => {
    const aligned = buildAlignedSeries(summary.monthly, summary.yearly);

    return [
      { name: 'Monthly Plan', data: aligned.monthly },
      { name: 'Yearly Plan', data: aligned.yearly },
    ];
  }, [summary]);

  const lastUpdate = summary.lastScheduledSyncAt
    ? dayjs(summary.lastScheduledSyncAt)
    : null;
  let availabilityLabel = 'Data updated daily (Last Update: Unavailable)';
  if (filters.loading) {
    availabilityLabel = 'Updating report…';
  } else if (lastUpdate?.isValid()) {
    availabilityLabel = `Data updated daily (Last Update: ${lastUpdate
      .utcOffset(-300)
      .format('MM/DD/YYYY hh:mm A')} EST)`;
  }

  const lineOptions = useMemo(() => {
    const reportDates = lineSeries[0].data.map((point) => point.x);
    const maxCount = lineSeries.reduce((acc, series) => {
      const seriesMax = series.data.reduce(
        (maxValue, point) => Math.max(maxValue, Number(point?.y || 0)),
        0
      );
      return Math.max(acc, seriesMax);
    }, 0);
    const boundedMax = Math.max(1, Math.ceil(maxCount));
    const tickAmount = Math.min(6, boundedMax);

    const renderTooltip = ({ dataPointIndex, w }) => {
      const colors = w?.config?.colors || [];
      const configuredSeries = Array.isArray(w?.config?.series)
        ? w.config.series
        : [];
      const firstPointX =
        configuredSeries
          .map((series) => series?.data?.[dataPointIndex]?.x)
          .find((value) => value !== null && value !== undefined) ?? null;
      const header = formatTooltipDate(firstPointX);
      const selectedRows = configuredSeries.map((series, seriesIdx) => {
        const name = series?.name || `Series ${seriesIdx + 1}`;
        const point = series?.data?.[dataPointIndex] || {};
        return {
          color: colors[seriesIdx] || '#111928',
          name,
          selected: getSafeNumber(point?.count),
          tillDate: getSafeNumber(point?.tillDate),
        };
      });

      if (selectedRows.length === 0) return '';

      const sectionTitleStyle =
        'font-size:12px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.04em;margin:10px 0 6px;';
      const rowBaseStyle =
        'display:flex;align-items:center;justify-content:space-between;gap:16px;padding:4px 0;font-size:13px;color:#0f172a;';
      const labelStyle =
        'display:flex;align-items:center;gap:8px;min-width:0;font-weight:500;';
      const valueStyle = 'font-weight:700;';

      const buildRows = (key) =>
        selectedRows
          .map(
            (row) => `<div style="${rowBaseStyle}">
                <span style="${labelStyle}">
                  <span style="width:10px;height:10px;border-radius:999px;background:${
                    row.color
                  };display:inline-block;flex:none;"></span>
                  <span>${row.name}</span>
                </span>
                <span style="${valueStyle}">${Math.round(row[key])}</span>
              </div>`
          )
          .join('');

      return `<div style="min-width:240px;padding:12px 14px;">
          ${
            header
              ? `<div style="font-size:14px;font-weight:700;color:#0f172a;margin-bottom:6px;">${header}</div>`
              : ''
          }
          <div style="${sectionTitleStyle}">Cumulative Count</div>
          ${buildRows('tillDate')}
          <div style="height:1px;background:#e2e8f0;margin:10px 0 2px;"></div>
          <div style="${sectionTitleStyle}">Daily Count</div>
          ${buildRows('selected')}
        </div>`;
    };

    return {
      chart: {
        type: 'line',
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      stroke: { curve: 'smooth', width: 3 },
      markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
      xaxis: {
        type: 'numeric',
        // Explicit bounds and numeric ticks retain both endpoint labels.
        ...(reportDates.length > 1
          ? {
              min: reportDates[0],
              max: reportDates[reportDates.length - 1],
              tickAmount: Math.min(6, reportDates.length - 1),
            }
          : {}),
        labels: {
          formatter: (value) => dayjs(Number(value)).format('MMM DD'),
          hideOverlappingLabels: false,
        },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        max: boundedMax,
        tickAmount,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: { formatter: (value) => `${Math.round(value)}` },
      },
      colors: ['#f03434', '#7f8bff'],
      dataLabels: { enabled: false },
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
      tooltip: { shared: true, intersect: false, custom: renderTooltip },
      legend: { position: 'top' },
    };
  }, [lineSeries]);

  return (
    <Card>
      <CardHeader
        title="Total Active Subscribers"
        subheader="Web Subscribers Only (Monthly/Yearly)"
        action={
          <IconButton
            title="Refresh"
            onClick={handleRefreshData}
            disabled={filters.loading}
          >
            <Iconify icon="ic:twotone-refresh" />
          </IconButton>
        }
      />
      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <Grid container>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <Stack direction="row" justifyContent="right">
              <Datepicker
                id="wssc_start_date"
                name="wssc_start_date"
                label="Start Date"
                defaultValue={filters.startDate}
                onChange={handleStartDateChange}
                sx={{ width: 200 }}
                disabled={filters.loading}
              />
              &nbsp;&nbsp;
              <Datepicker
                id="wssc_end_date"
                name="wssc_end_date"
                label="End Date"
                defaultValue={filters.endDate}
                onChange={handleEndDateChange}
                sx={{ width: 200 }}
                disabled={filters.loading}
                disableFutureDate
              />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: 'right' }}
              aria-live="polite"
              title="Based on the scheduled update time. Update completion is not confirmed."
            >
              {availabilityLabel}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* {filters.loading && (
        <div className="loading-banner">Updating report…</div>
      )}
      <div className="report-line-chart">
        {lineSeries.every((series) => series.data.length === 0) ? (
          <div className="trend-empty">No data for selected range.</div>
        ) : (
          <ReactApexChart
            options={lineOptions}
            series={lineSeries}
            type="line"
            height={320}
          />
        )}
      </div> */}
      <Grid item xs={12}>
        <Stack sx={{ marginTop: 2 }}>
          <ReactApexChart
            type="line"
            series={lineSeries}
            options={lineOptions}
            height={364}
          />
        </Stack>
      </Grid>
    </Card>
  );
};

TotalSubscriber.propTypes = {
  selectedChannel: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  updateFilters: PropTypes.func.isRequired,
};

export default TotalSubscriber;
